import { ArrowRight } from "lucide-react";
import { useNavigate } from "react-router";
import { useHentAlleEfteruddannelsesperiodeIndberetninger } from "@/hooks/useHentEfteruddannelsesperiode";
import { eftPath } from "@cle/constants/routes";
import { useReportingLessonsStatus } from "@cle/hooks/useReportingLessonsStatus";
import {
  buildCurrentPeriodLessonsStatus,
  selectCurrentPeriod,
} from "@cle/utils/currentPeriodLessonsStatus";

const SummarySkeleton = () => (
  <div className="cle-current-period-status placeholder-glow" aria-hidden>
    <span className="placeholder rounded" style={{ width: "9rem" }} />
    <span
      className="placeholder rounded"
      style={{ height: "1.5rem", width: "10rem" }}
    />
    <span className="placeholder rounded" style={{ width: "20rem" }} />
    <span
      className="placeholder rounded"
      style={{ height: "1.75rem", width: "8rem" }}
    />
  </div>
);

/**
 * Status for den igangvaerende efteruddannelsesperiode, vist i tomtilstanden paa forsiden.
 *
 * Komponenten henter selv sine data frem for at faa dem trukket ned gennem
 * PendingReportsContent: den renderes kun, naar der ikke er afventende indberetninger, og
 * saa skal kaldene heller ikke foretages i det almindelige tilfaelde.
 */
const CurrentPeriodLessonsSummary = () => {
  const navigate = useNavigate();
  const { data: periods, isLoading: isLoadingPeriods } =
    useHentAlleEfteruddannelsesperiodeIndberetninger();
  const currentPeriod = selectCurrentPeriod(periods);

  // periodeTilDato er den EKSKLUSIVE slutdato, og det er den, endepunktet forventer.
  // periodeTilDatoMinusEnDag er kun til visning; sendes den, tabes et kursus dateret paa
  // periodens sidste dag — tavst, med et lidt for lavt tal til foelge.
  const {
    data: lessonsStatus,
    isError: isLessonsError,
    isLoading: isLoadingLessons,
  } = useReportingLessonsStatus({
    periodStart: currentPeriod?.periodeFraDato,
    periodEnd: currentPeriod?.periodeTilDato,
  });

  if (isLoadingPeriods) {
    return <SummarySkeleton />;
  }

  // Ingen nuvaerende periode, eller perioderne kunne ikke hentes. Tallet er en tilfoejelse
  // til en i oevrigt beroligende besked, saa det skal udeblive i stilhed frem for at give
  // en fejlmeddelelse.
  if (!currentPeriod) {
    return null;
  }

  const { figureText, rangeLine, transferredLine } =
    buildCurrentPeriodLessonsStatus({
      availableLessons: lessonsStatus?.availableLessons,
      lessonsKnown: !isLessonsError,
      periodFromDate: currentPeriod.periodeFraDato,
      periodToDateMinusOneDay: currentPeriod.periodeTilDatoMinusEnDag,
      transferredLessons: currentPeriod.lektionerOverfoertTilDenne?.sum ?? 0,
    });

  return (
    <div className="cle-current-period-status">
      <p className="cle-current-period-status__label">Nuværende periode</p>

      {isLoadingLessons ? (
        <span className="placeholder-glow">
          <span
            className="placeholder rounded"
            style={{ height: "1.5rem", width: "10rem" }}
          />
        </span>
      ) : (
        <p
          className={`cle-current-period-status__figure${
            isLessonsError
              ? " cle-current-period-status__figure--unavailable"
              : ""
          }`}
        >
          {isLessonsError ? `Lektionstal ${figureText}` : figureText}
        </p>
      )}

      <p className="cle-current-period-status__range">{rangeLine}</p>
      {transferredLine ? (
        <p className="cle-current-period-status__transferred">
          {transferredLine}
        </p>
      ) : null}
      <button
        type="button"
        id="cle-vis-nuvaerende-periode"
        className="btn btn-dark btn-sm cle-current-period-status__cta d-flex align-items-center gap-2 rounded-3 fw-semibold"
        onClick={() => {
          void navigate(
            eftPath(
              `indberetninger/${currentPeriod.efteruddannelsesperiodeId}`,
            ),
          );
        }}
      >
        Vis perioden
        <ArrowRight size={17} strokeWidth={2} />
      </button>
    </div>
  );
};

export default CurrentPeriodLessonsSummary;
